import { useRef, useState } from "react";
import { Loader2, ImagePlus, Camera, X, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, apiErr } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

// Downscale any accepted image to a JPEG good enough for AI reading, under the size limit.
function readAndCompress(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Imagen inválida"));
      img.onload = () => {
        const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.9));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export function ImportDialog({ open, onClose, onDraft }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [failed, setFailed] = useState(false);

  const reset = () => { setPreview(null); setFailed(false); setAnalyzing(false); };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error("La imagen supera 10 MB"); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WEBP."); return;
    }
    try {
      const dataUrl = await readAndCompress(file);
      setPreview(dataUrl); setFailed(false);
    } catch (err) { toast.error(err.message || "No se pudo procesar la imagen"); }
  };

  const analyze = async () => {
    if (!preview) return;
    setAnalyzing(true); setFailed(false);
    try {
      const { data } = await api.post("/planillas/imports", {
        image_base64: preview, mime_type: "image/jpeg",
      });
      if (data.draft) {
        onDraft(data.draft, data.import?.id);
        reset();
      } else {
        setFailed(true);
        toast.error(data.message || "No pudimos interpretar la planilla");
      }
    } catch (e) {
      setFailed(true);
      toast.error(apiErr(e));
    } finally { setAnalyzing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="h-5 w-5 text-[#2563EB]" /> Importar desde imagen
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#68756F]">
            Sube una fotografía o escaneo de una planilla existente y TUPU preparará un borrador
            editable para ti. Tú revisas y guardas.
          </p>

          {preview ? (
            <div className="relative">
              <img src={preview} alt="Planilla a importar" data-testid="import-preview"
                className="max-h-80 w-full rounded-xl border border-[#E2E8F0] object-contain bg-[#F8FAFC]" />
              <button onClick={() => reset()} disabled={analyzing}
                data-testid="import-clear-btn"
                className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-white/90 shadow">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} data-testid="import-dropzone"
              className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] py-12 text-[#68756F] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]">
              <ImagePlus className="h-10 w-10" />
              <span className="text-sm font-medium">Sube una imagen o toma una foto</span>
              <span className="text-xs text-slate-400">JPG, PNG o WEBP · máx. 10 MB</span>
            </button>
          )}

          {failed && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>No pudimos interpretar correctamente esta planilla. Puedes intentarlo nuevamente con una foto más nítida o crearla manualmente.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={analyzing}
              data-testid="import-choose-btn" className="h-11 rounded-xl">
              <Camera className="mr-2 h-4 w-4" /> {preview ? "Cambiar imagen" : "Elegir imagen"}
            </Button>
            <Button onClick={analyze} disabled={!preview || analyzing}
              data-testid="import-analyze-btn" className="h-11 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8]">
              {analyzing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analizando…</> : <><Sparkles className="mr-2 h-4 w-4" /> Analizar planilla</>}
            </Button>
          </div>
          {analyzing && <p className="text-center text-xs text-slate-400">Esto puede tardar unos segundos…</p>}
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} capture="environment"
          data-testid="import-file-input" className="hidden" onChange={onPick} />
      </DialogContent>
    </Dialog>
  );
}
